import React, { useCallback, useMemo, useState } from 'react';
import { FieldType, StringFieldType } from '~/src/entities/project';
import {
  type FieldOption,
  type FieldMappingOptions,
  type ContactFieldGroup,
} from '~/src/entities/fieldMapping';
import { getFilteredFieldMappingOptions } from '~/src/pages/Populate/FieldMappingProvider/fieldMappingUtils';

type FieldMappingContextType = {
  options: FieldMappingOptions;
  loading: boolean;
  error: string | null;
  getFilteredOptions: (
    variableType: FieldType | StringFieldType,
    isRoleCard: boolean,
    contactId?: number,
  ) => FieldOption[];
  getOptionsLookup: (
    variableType: FieldType | StringFieldType,
    isRoleCard: boolean,
    contactId?: number,
  ) => Map<string, FieldOption>;
  addContactFieldGroup: (
    contactId: number,
    fieldGroup: ContactFieldGroup,
  ) => void;
  hasContactFields: (contactId: number) => boolean;
  openWidgetFieldId: string | null;
  setOpenWidgetFieldId: (fieldId: string | null) => void;
};

const FieldMappingContext = React.createContext<
  FieldMappingContextType | undefined
>(undefined);

type FieldMappingProviderProps = React.PropsWithChildren<{
  options: FieldMappingOptions;
  loading: boolean;
  error: string | null;
}>;

export const FieldMappingProvider = ({
  children,
  options: initialOptions,
  loading,
  error,
}: FieldMappingProviderProps) => {
  const [openWidgetFieldId, setOpenWidgetFieldId] = useState<string | null>(
    null,
  );
  const [additionalContactFields, setAdditionalContactFields] = useState<
    Record<string, ContactFieldGroup>
  >({});

  const allOptions = useMemo<FieldMappingOptions>(
    () => ({
      matter_fields: initialOptions.matter_fields,
      contact_fields: {
        ...initialOptions.contact_fields,
        ...additionalContactFields,
      },
    }),
    [initialOptions, additionalContactFields],
  );

  const getFilteredOptions = useCallback(
    (
      variableType: FieldType | StringFieldType,
      isRoleCard: boolean,
      contactId?: number,
    ) => {
      return getFilteredFieldMappingOptions(
        allOptions,
        variableType,
        isRoleCard,
        contactId,
      );
    },
    [allOptions],
  );

  const getOptionsLookup = useCallback(
    (
      variableType: FieldType | StringFieldType,
      isRoleCard: boolean,
      contactId?: number,
    ): Map<string, FieldOption> => {
      const options = getFilteredFieldMappingOptions(
        allOptions,
        variableType,
        isRoleCard,
        contactId,
      );
      return new Map(options.map((o) => [o.normalized_attribute, o]));
    },
    [allOptions],
  );

  const addContactFieldGroup = useCallback(
    (contactId: number, fieldGroup: ContactFieldGroup) => {
      setAdditionalContactFields((prev) => ({
        ...prev,
        [String(contactId)]: fieldGroup,
      }));
    },
    [],
  );

  const hasContactFields = useCallback(
    (contactId: number) => {
      return String(contactId) in allOptions.contact_fields;
    },
    [allOptions.contact_fields],
  );

  const value = useMemo(
    () => ({
      options: allOptions,
      loading,
      error,
      getFilteredOptions,
      getOptionsLookup,
      addContactFieldGroup,
      hasContactFields,
      openWidgetFieldId,
      setOpenWidgetFieldId,
    }),
    [
      allOptions,
      loading,
      error,
      getFilteredOptions,
      getOptionsLookup,
      addContactFieldGroup,
      hasContactFields,
      openWidgetFieldId,
    ],
  );

  return (
    <FieldMappingContext.Provider value={value}>
      {children}
    </FieldMappingContext.Provider>
  );
};

export const useFieldMappingContext = () => {
  const context = React.useContext(FieldMappingContext);
  if (!context) {
    throw new Error(
      'useFieldMappingContext must be used within a FieldMappingProvider',
    );
  }
  return context;
};
